/* eslint-disable */
import cloneDeep from 'lodash/cloneDeep';
import {useCallback, useEffect, useState} from 'react';

// @mui/material components
import Button from 'components/CustomButtons/Button.js';
import CustomInput from 'components/CustomInput/CustomInput.js';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Snackbar from 'components/Snackbar/Snackbar.js';

import Warning from '@mui/icons-material/Warning';

// Styles
import {makeStyles} from '@mui/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/tenantStyle.js';
import {toast} from 'react-toastify';
import {PromptAlert} from '../../features/ux/RouterPrompt';
import {isValidURL} from '../../utils';
import { useGetTenantQuery } from 'api/tenant.api';
import { useUpdateTenantConnectionKeyMutation } from 'api/tenant.api';
import { useUpdateTenantIntegrationSettingsMutation } from 'api/tenant.api';

import SecretInput from "../../components/CustomInput/SecretInput";

const useStyles = makeStyles(styles);

const IntegrationSettingsTab = ({onSuccessTab, tab, setDirtyTabStatus}) => {
  const classes = useStyles();
  const {data: tenant} = useGetTenantQuery();
  
  useEffect(() => {
    if (tenant) {
      setResetInputs(prev => prev + 1);
      setViewIsDirty(false)
    }
  }, [tenant]);  
  
  const [updateConnectionKey] = useUpdateTenantConnectionKeyMutation();
  const [updateIntegrationSettings] = useUpdateTenantIntegrationSettingsMutation();

  const [viewIsDirty, setViewIsDirty] = useState(false);
  const [showWarningAlert, setShowWarningAlert] = useState(false);

  const [connectionKey, setConnectionKey] = useState('');
  const [newConnectionKey, setNewConnectionKey] = useState(false);

  const [webhookUrl, setWebhookUrl] = useState(tenant.webhook_url);
  const [webhookUrlState, setWebhookUrlState] = useState('');
  const [webhookUrlError, setWebhookUrlError] = useState('');
  const [webhookKey, setWebhookKey] = useState(tenant.webhook_verification_key|| undefined);
  const [webhookKeyState, setWebhookKeyState] = useState('');

  const [webhookApiKey, setWebhookApiKey] = useState(tenant?._webhook_api_key|| undefined);
  const [webhookApiKeyState, setWebhookApiKeyState] = useState('');

  useEffect(() => {
    if (setDirtyTabStatus) {
      setDirtyTabStatus(viewIsDirty);
    }
  }, [viewIsDirty]);

  const [cartRetrievalUrl, setCartRetrievalUrl] = useState(
    tenant.cart_retrieval_url
  );
  const [cartRetrievalUrlState, setCartRetrievalUrlState] = useState('');
  const [cartRetrievalUrlError, setCartRetrievalUrlError] = useState('');

  const [returnCartRetrievalUrl, setReturnCartRetrievalUrl] = useState(
    tenant.return_cart_retrieval_url
  );
  const [returnCartRetrievalUrlState, setReturnCartRetrievalUrlState] =
    useState('');
  const [returnCartRetrievalUrlError, setReturnCartRetrievalUrlError] =
    useState('');

  const [tokenRetrievalUrl, setTokenRetrievalUrl] = useState(
    tenant.token_retrieval_url
  );
  const [tokenRetrievalUrlState, setTokenRetrievalUrlState] = useState('');
  const [tokenRetrievalUrlError, setTokenRetrievalUrlError] = useState('');

  const [callbackApiKey, setCallbackApiKey] = useState(tenant.callback_api_key || undefined);
  const [callbackApiKeyState, setCallbackApiKeyState] = useState('');

  const [keySyncUrl, setKeySyncUrl] = useState(tenant.key_sync_url);
  const [keySyncUrlState, setKeySyncUrlState] = useState('');
  const [keySyncUrlError, setKeySyncUrlError] = useState('');

  const [syncApiKey, setSyncApiKey] = useState(tenant.sync_api_key || undefined);
  const [syncApiKeyState, setSyncApiKeyState] = useState('');

  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackbarMessage, setSnackBarMessage] = useState('');

  const [resetInputs, setResetInputs] = useState(0)

  const generateConnectionKey = () => {
    setConnectionKey(
      Math.random().toString(36).substring(2, 8) +
      Math.random().toString(36).substring(2, 8) +
      Math.random().toString(36).substring(2, 8)
    );
    setNewConnectionKey(true);
    setViewIsDirty(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(connectionKey);
  };

  const saveNewConnectionKey = async () => {
    await updateConnectionKey({key: connectionKey}).unwrap();
    setNewConnectionKey(false);
  };

  const verifyLength = (value, original, length, required = true) => {
    if (value && value.length >= length) {
      if (value !== original) setViewIsDirty(true);
      return true;
    } else if (!required && (!value || value.length === 0)) {
      if (value !== original) setViewIsDirty(true);
      return true;
    }
    return false;
  };

  const saveChanges = async () => {
    let error = false;

    if (webhookUrl?.trim() === '') {
      setWebhookUrlState('error');
      setWebhookUrlError(
        'Field must contain at least 12 alphanumeric or special characters'
      );
      error = true;
    }

    if (cartRetrievalUrl?.trim() === '') {
      setCartRetrievalUrlState('error');
      setCartRetrievalUrlError(
        'Field must contain at least 12 alphanumeric or special characters'
      );
      error = true;
    }

    if (tokenRetrievalUrl?.trim() === '') {
      setTokenRetrievalUrlState('error');
      setTokenRetrievalUrlError(
        'Field must contain at least 12 alphanumeric or special characters'
      );
      error = true;
    }

    if (keySyncUrl?.trim() === '') {
      setKeySyncUrlState('error');
      setKeySyncUrlError(
        'Field must contain at least 12 alphanumeric or special characters'
      );
      error = true;
    }

    if (error) {
      return;
    }

    try {
      let updatedTenant = cloneDeep(tenant);
      updatedTenant.webhook_url = webhookUrl;
      updatedTenant.webhook_verification_key = webhookKey|| undefined;
      updatedTenant.cart_retrieval_url = cartRetrievalUrl;
      updatedTenant.return_cart_retrieval_url = returnCartRetrievalUrl;
      updatedTenant.token_retrieval_url = tokenRetrievalUrl;
      updatedTenant.callback_api_key = callbackApiKey|| undefined;
      updatedTenant.key_sync_url = keySyncUrl;
      updatedTenant.sync_api_key = syncApiKey;
      updatedTenant._webhook_api_key = webhookApiKey || undefined;

      if (newConnectionKey) {
        await saveNewConnectionKey();
      }

      await updateIntegrationSettings({tenantIntegrationSettings: updatedTenant}).unwrap();

      setViewIsDirty(false);
      setResetInputs(prev => prev + 1);

      toast.success('Changes saved successfully', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });

      if (onSuccessTab) {
        onSuccessTab(tab.name);
      }
    } catch (err) {
      console.error('Error saving tenant data:', err);
      toast.error('An error occurred while trying to save the changes', {
        position: toast.POSITION.BOTTOM_CENTER,
        autoClose: 2000,
        hideProgressBar: true,
      });
    }
  };

  const revertForm = () => {
    setWebhookUrl(tenant.webhook_url);
    setWebhookUrlState('');

    setCartRetrievalUrl(tenant.cart_retrieval_url);
    setCartRetrievalUrlState('');

    setReturnCartRetrievalUrl(tenant.return_cart_retrieval_url);
    setReturnCartRetrievalUrlState('');

    setTokenRetrievalUrl(tenant.token_retrieval_url);
    setTokenRetrievalUrlState('');

    setCallbackApiKey(tenant.callback_api_key || undefined);
    setCallbackApiKeyState('');

    setKeySyncUrl(tenant.key_sync_url);
    setKeySyncUrlState('');

    setSyncApiKey(tenant.sync_api_key || undefined);
    setSyncApiKeyState('');

    setNewConnectionKey(false);

    setWebhookKey(tenant?.webhook_verification_key || undefined);
    setWebhookKeyState('');

    setWebhookApiKey(tenant?._webhook_api_key || undefined);
    setWebhookApiKeyState('');
    setResetInputs(prev => prev + 1);

    setViewIsDirty(false);
  };

  const hasErrors = useCallback(() => {
    return (
      webhookApiKeyState === 'error' ||
      webhookUrlState === 'error' ||
      cartRetrievalUrlState === 'error' ||
      tokenRetrievalUrlState === 'error' ||
      callbackApiKeyState === 'error' ||
      keySyncUrlState === 'error' ||
      syncApiKeyState === 'error' ||
      returnCartRetrievalUrlState === 'error' ||
      webhookKeyState === 'error'
    );
  }, [
    webhookApiKeyState,
    webhookUrlState,
    cartRetrievalUrlState,
    tokenRetrievalUrlState,
    callbackApiKeyState,
    keySyncUrlState,
    syncApiKeyState,
    returnCartRetrievalUrlState,
    webhookKeyState,
  ]);

  return (
    <GridContainer>
      {showWarningAlert && (
        <PromptAlert
          message='You have unsaved changes. Are you sure you want to discard them?'
          title='Discard Changes'
          onConfirm={() => {
            revertForm();
            setShowWarningAlert(false);
          }}
          onCancel={() => setShowWarningAlert(false)}
        />
      )}
      <Snackbar
        place='tr'
        color='rose'
        icon={Warning}
        message={snackbarMessage}
        open={showSnackBar}
        closeNotification={() => setShowSnackBar(false)}
        close
      />
      <GridItem xs={12}>
        <h4>Tenant ID: {tenant.tenant_id}</h4>
        {tenant.connection_key_hash === '' || newConnectionKey ? (
          newConnectionKey ? (
            <>
              <h4>Connection Key: {connectionKey}</h4>
              <Button
                color='rose'
                onClick={copyToClipboard}
                className={classes.keyButton}
              >
                Copy Key to Clipboard
              </Button>
            </>
          ) : (
            <Button
              color='rose'
              onClick={generateConnectionKey}
              className={classes.keyButton}
            >
              Generate Connection Key
            </Button>
          )
        ) : (
          <>
            <h4>Connection Key: ************************</h4>
            <Button
              color='rose'
              onClick={generateConnectionKey}
              className={classes.keyButton}
            >
              Generate New Connection Key
            </Button>
          </>
        )}
      </GridItem>
      <GridItem xs={6}>
        <CustomInput
          helperText={
            webhookUrlState === 'error'
              ? webhookUrlError ?? 'An error has occurred'
              : false
          }
          success={webhookUrlState === 'success'}
          error={webhookUrlState === 'error'}
          labelText='Webhook URL'
          id='webhook'
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            value: webhookUrl,
            onChange: (event) => {
              if (
                verifyLength(event.target.value, webhookUrl, 12, false) &&
                isValidURL(event.target.value, webhookUrl)
              ) {
                setWebhookUrlState('success');
                setWebhookUrlError('');
              } else {
                setWebhookUrlState('error');
                setWebhookUrlError(
                  'The field must contain at least 12 alphanumeric or special characters and be a valid URL'
                );
              }
              setWebhookUrl(event.target.value);
            },
            autoComplete: 'off',
          }}
        />
        <CustomInput
          helperText={
            cartRetrievalUrlState === 'error'
              ? cartRetrievalUrlError ?? 'An error has occurred'
              : false
          }
          success={cartRetrievalUrlState === 'success'}
          error={cartRetrievalUrlState === 'error'}
          labelText='Cart Retrieval URL'
          id='cartretrieval'
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            value: cartRetrievalUrl,
            onChange: (event) => {
              if (
                verifyLength(event.target.value, cartRetrievalUrl, 12, false) &&
                isValidURL(event.target.value, cartRetrievalUrl)
              ) {
                setCartRetrievalUrlState('success');
                setCartRetrievalUrlError('');
              } else {
                setCartRetrievalUrlState('error');
                setCartRetrievalUrlError(
                  'The field must contain at least 12 alphanumeric or special characters and be a valid URL'
                );
              }
              setCartRetrievalUrl(event.target.value);
            },
            autoComplete: 'off',
          }}
        />
        <CustomInput
          helperText={
            tokenRetrievalUrlState === 'error'
              ? tokenRetrievalUrlError ?? 'An error has occurred'
              : false
          }
          success={tokenRetrievalUrlState === 'success'}
          error={tokenRetrievalUrlState === 'error'}
          labelText='Token Retrieval URL'
          id='tokenretrieval'
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            value: tokenRetrievalUrl,
            onChange: (event) => {
              if (
                verifyLength(
                  event.target.value,
                  tokenRetrievalUrl,
                  12,
                  false
                ) &&
                isValidURL(event.target.value, tokenRetrievalUrl)
              ) {
                setTokenRetrievalUrlState('success');
                setTokenRetrievalUrlError('');
              } else {
                setTokenRetrievalUrlState('error');
                setTokenRetrievalUrlError(
                  'The field must contain at least 12 alphanumeric or special characters and be a valid URL'
                );
              }
              setTokenRetrievalUrl(event.target.value);
            },
            autoComplete: 'off',
          }}
        />
        <CustomInput
          helperText={
            keySyncUrlState === 'error'
              ? keySyncUrlError ?? 'An error has occurred'
              : false
          }
          success={keySyncUrlState === 'success'}
          error={keySyncUrlState === 'error'}
          labelText='Key Sync URL'
          id='keysyncurl'
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            value: keySyncUrl,
            onChange: (event) => {
              if (
                verifyLength(event.target.value, keySyncUrl, 12, false) &&
                isValidURL(event.target.value, keySyncUrl)
              ) {
                setKeySyncUrlState('success');
                setKeySyncUrlError('');
              } else {
                setKeySyncUrlState('error');
                setKeySyncUrlError(
                  'The field must contain at least 12 alphanumeric or special characters and be a valid URL'
                );
              }
              setKeySyncUrl(event.target.value);
            },
            autoComplete: 'off',
          }}
        />
      </GridItem>
      <GridItem xs={6}>
        {/* Webhook API Key */}
        <SecretInput
          inputId='webhookapikey'
          labelText='Webhook API Key'
          inputValue={tenant?.hasWebhookApiKey}
          onValidSecret={(value) => {
            setWebhookApiKey(value)
            setWebhookApiKeyState('success');
            setViewIsDirty(true)
          }}
          onInvalidSecret={() => {
            setWebhookApiKeyState('error');
            setViewIsDirty(true)
          }}
          validationRules={{ minLength: 12 }}
          resetInput={resetInputs}
        ></SecretInput>
        {/* Webhook Verification Key */}
        <SecretInput
          inputId='webhookkey'
          labelText='Webhook Verification Key'
          inputValue={tenant?.hasWebhookVerificationKey}
          onValidSecret={(value) => {
            setWebhookKey(value);
            setWebhookKeyState('success');
            setViewIsDirty(true)
          }}
          onInvalidSecret={() => {
            setWebhookKeyState('error');
            setViewIsDirty(true)
          }}
          validationRules={{ minLength: 8 }}
          resetInput={resetInputs}
        />
        <CustomInput
          helperText={
            returnCartRetrievalUrlState === 'error'
              ? returnCartRetrievalUrlError ?? 'An error has occurred'
              : false
          }
          success={returnCartRetrievalUrlState === 'success'}
          error={returnCartRetrievalUrlState === 'error'}
          labelText='Return Cart Retrieval URL'
          id='returncartretrieval'
          formControlProps={{
            fullWidth: true,
          }}
          inputProps={{
            value: returnCartRetrievalUrl,
            onChange: (event) => {
              if (
                verifyLength(
                  event.target.value,
                  returnCartRetrievalUrl,
                  12,
                  false
                ) &&
                isValidURL(event.target.value, returnCartRetrievalUrl)
              ) {
                setReturnCartRetrievalUrlState('success');
                setReturnCartRetrievalUrlError('');
              } else {
                setReturnCartRetrievalUrlState('error');
                setReturnCartRetrievalUrlError(
                  'The field must contain at least 12 alphanumeric or special characters and be a valid URL'
                );
              }
              setReturnCartRetrievalUrl(event.target.value);
            },
            autoComplete: 'off',
          }}
        />
        {/* Callback API Key */}
        <SecretInput
          inputId='callbackapikey'
          labelText='Callback API Key'
          inputValue={tenant?.hasCallbackApiKey}
          onValidSecret={(value) => {
            setCallbackApiKey(value);
            setCallbackApiKeyState('success');
            setViewIsDirty(true)
          }}
          onInvalidSecret={() => {
            setCallbackApiKeyState('error');
            setViewIsDirty(true)
          }}
          validationRules={{ minLength: 12 }}
          resetInput={resetInputs}
        />
        {/* Sync API Key */}
        <SecretInput
          inputId='syncapikey'
          labelText='Sync API Key'
          inputValue={tenant?.hasSyncApiKey}
          onValidSecret={(value) => {
            setSyncApiKey(value);
            setSyncApiKeyState('success');
            setViewIsDirty(true)
          }}
          onInvalidSecret={() => {
            setSyncApiKeyState('error');
            setViewIsDirty(true)
          }}
          validationRules={{ minLength: 12 }}
          resetInput={resetInputs}
        />
      </GridItem>
      <GridItem xs={12}>
        <Button
          color='rose'
          onClick={saveChanges}
          className={classes.registerButton}
          disabled={
            (!newConnectionKey && !viewIsDirty) || !tab.active || hasErrors()
          }
        >
          Save Changes
        </Button>
        <Button
          color='secondary'
          onClick={() => setShowWarningAlert(true)}
          className={classes.registerButton}
          disabled={!viewIsDirty}
        >
          Cancel
        </Button>
      </GridItem>
    </GridContainer>
  );
};

export default IntegrationSettingsTab;
